<template>
  <div :id="id" :class="filteredStyles" class="diff-group">
    <span v-if="changeBadge" :class="'changeBadge-' + changeType">{{
      changeBadge
    }}</span>
    <div
      :id="id + '_head'"
      :class="collapsibleClasses"
      role="heading"
      :aria-expanded="collapsed ? 'false' : 'true'"
      @click="toggleCollapse"
    >
      <div
        v-if="isHeading"
        role="heading"
        :class="{
          'collapsible-title': collapsible,
          h1: level === 1,
          h2: level === 2,
          h3: level === 3,
          h4: level === 4,
          h5: level === 5,
          h6: level === 6
        }"
        style="margin-bottom: 0.1em; padding-bottom: 0.1em"
      >
        {{ label }}
      </div>
      <div v-else :class="{ 'collapsible-title': collapsible }">
        {{ label }}
      </div>
    </div>
    <p v-if="hint">{{ hint }}</p>
    <div
      v-if="!collapsible || !collapsed"
      class="group-children"
      role="group"
      :aria-describedby="id + '_head'"
    >
      <div v-for="(child, idx) in children" :key="idx + 1">
        <div v-if="child">
          <template v-if="child.children && child.value">
            <template v-if="child.previous">
              <attribute-diff
                :label="child.texts && child.texts.label"
                :valueAfter="child.value"
                :valueBefore="child.previous"
                :styles="child.styles"
                :changeType="child.changeType"
                :suppress-badges="suppressBadges"
              >
                <cat-diff-group
                  :children="child.children"
                  :suppress-badges="true"
                />
              </attribute-diff>
            </template>
            <template v-else>
              <attribute-diff-single
                :item="child"
                :suppress-badges="suppressBadges"
              >
                <cat-diff-group
                  :children="child.children"
                  :suppress-badges="true"
                />
              </attribute-diff-single>
            </template>
          </template>
          <cat-diff-group
            v-else-if="child.children"
            :children="child.children"
            :label="child.texts && child.texts.label"
            :hint="child.texts && child.texts.hint"
            :styles="child.styles"
            :level="child.level"
            :changeType="child.changeType"
            :suppress-badges="suppressBadges"
          />
          <attribute-diff
            v-else-if="child.previous"
            :label="child.texts && child.texts.label"
            :valueAfter="child.value"
            :valueBefore="child.previous"
            :styles="child.styles"
            :changeType="child.changeType"
            :suppress-badges="suppressBadges"
          />
          <div
            v-else-if="child.styles && child.styles.indexOf('line') != -1"
            :class="child.styles"
          >
            {{ child.texts.label }}
          </div>
          <div v-else>
            <attribute-diff-single
              :item="child"
              :suppress-badges="suppressBadges"
            >
              <cat-diff-group
                v-if="child.children"
                :children="child.children"
                :suppress-badges="true"
              />
            </attribute-diff-single>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import AttributeDiff from "./cat-attribute-diff";
import AttributeDiffSingle from "./cat-attribute-diff-single.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatDiffGroup",
  components: { AttributeDiff, AttributeDiffSingle },
  props: {
    children: [Array],
    label: [String],
    hint: [String],
    styles: [Array],
    changeType: [String],
    suppressBadges: {
      type: Boolean,
      default: false
    },
    level: {
      type: Number,
      default: 3
    }
  },
  data: function () {
    return {
      collapsible: this.styles && this.styles.indexOf("collapse") != -1,
      collapsed: this.styles && this.styles.indexOf("expanded") == -1,
      isHeading: this.styles && this.styles.indexOf("heading") != -1,
      id: Math.random() * 1000000
    };
  },
  methods: {
    toggleCollapse: function () {
      this.collapsed = !this.collapsed;
    },
    changeBadgeEval: function (type) {
      if (!type || "none" === type) {
        return null;
      }
      return this.resolveTxt("diff.badge." + type, type);
    }
  },
  computed: {
    collapsibleClasses: function () {
      return {
        "collapsible icon-angle-right group-label": this.collapsible,
        "static-title group-label": !this.collapsible,
        empty: !this.label || this.label === ""
      };
    },
    filteredStyles: function () {
      var meaningfulStyles = ["collapse", "heading", "expanded", "line"];
      var changeType = this.changeType
        ? ["changeType", "radius-r", "changeType-" + this.changeType]
        : null;
      // We only want padding top for non-mod, with label or a filing declaration (no label but requires padding on top)
      if (
        changeType &&
        this.changeType !== "mod" &&
        (this.label ||
          (this.styles && this.styles.indexOf("filing-declaration") >= 0))
      ) {
        changeType.push("pts");
      }
      if (!this.styles) {
        return changeType;
      }
      var styls = this.styles.filter((v) => {
        return meaningfulStyles.indexOf(v) == -1;
      });
      if (changeType) {
        styls.push(changeType);
      }

      styls.push("diff-group");
      if (this.styles.indexOf("collapse") != -1) {
        styls.push("expandable");
      }
      if (this.suppressBadges) {
        styls.push("suppressBadges");
      }

      return styls;
    },
    changeBadge: function () {
      return !this.suppressBadges && this.changeBadgeEval(this.changeType);
    }
  }
});
</script>
